/**
 * Phase 8B — Create / edit a section in an MIS.
 *   Revenue / Expense: title + optional show_percentage
 *   Formula: title + expression editor (letter chips + operators) + show_percentage
 */

import { FunctionComponent, useEffect, useMemo, useState } from "react";
import {
  MisSection,
  MisSectionType,
  SECTION_TYPE_HINT,
  SECTION_TYPE_LABEL,
  orderToLetter,
  renderFormula,
} from "../models/mis_model";

interface Props {
  open: boolean;
  initial?: MisSection | null;
  existingSections: MisSection[]; // for formula editor + percentage-of picker
  onClose: () => void;
  onSubmit: (payload: {
    section_type: MisSectionType;
    title: string;
    formula_expression?: string | null;
    show_percentage: boolean;
    percentage_of_section_id?: string | null;
  }) => Promise<void>;
}

const OPERATORS = ["+", "−", "×", "÷", "%", "(", ")"];
const OPERATOR_TO_TOKEN: Record<string, string> = {
  "+": "+",
  "−": "-",
  "×": "*",
  "÷": "/",
  "%": "%",
  "(": "(",
  ")": ")",
};

/**
 * Append `next` to `prev`, inserting a single space between them only when
 * needed for readability. Handles both fresh appends and re-appends after
 * a backspace removed the trailing space.
 */
const smartAppend = (prev: string, next: string): string => {
  if (prev.length === 0) return next;
  const lastChar = prev[prev.length - 1];
  const firstChar = next[0];
  // No space adjacent to opening paren or before closing paren.
  const skipSpace =
    lastChar === " " || lastChar === "(" || firstChar === ")";
  return prev + (skipSpace ? "" : " ") + next;
};

const SectionModal: FunctionComponent<Props> = ({
  open,
  initial,
  existingSections,
  onClose,
  onSubmit,
}) => {
  const isEdit = !!initial;
  const [type, setType] = useState<MisSectionType>("revenue");
  const [title, setTitle] = useState("");
  const [formulaExpr, setFormulaExpr] = useState("");
  const [showPct, setShowPct] = useState(false);
  const [pctOfId, setPctOfId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sections available for reference are those with lower display_order than
  // *this* section (or all of them, when creating a new one at the end).
  const selfOrder = initial?.display_order ?? existingSections.length;
  const earlierSections = useMemo(
    () => existingSections.filter((s) => s.display_order < selfOrder),
    [existingSections, selfOrder],
  );

  const sectionOrderById = useMemo<Record<string, number>>(() => {
    const map: Record<string, number> = {};
    for (const s of existingSections) map[s.id] = s.display_order;
    return map;
  }, [existingSections]);

  const renderedFormula = useMemo(
    () => renderFormula(formulaExpr, sectionOrderById),
    [formulaExpr, sectionOrderById],
  );

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setType(initial.section_type);
      setTitle(initial.title);
      setFormulaExpr(initial.formula_expression ?? "");
      setShowPct(initial.show_percentage);
      setPctOfId(initial.percentage_of_section_id ?? null);
    } else {
      setType("revenue");
      setTitle("");
      setFormulaExpr("");
      setShowPct(false);
      setPctOfId(null);
    }
    setError(null);
    setSaving(false);
  }, [open, initial]);

  if (!open) return null;

  const insertToken = (sectionId: string) => {
    setFormulaExpr((prev) => smartAppend(prev, `{${sectionId}}`));
  };
  const insertOperator = (op: string) => {
    setFormulaExpr((prev) => smartAppend(prev, OPERATOR_TO_TOKEN[op]));
  };
  const clearFormula = () => setFormulaExpr("");
  const backspace = () =>
    setFormulaExpr((prev) => {
      // Strip trailing whitespace so we don't leave hanging spaces.
      const s = prev.replace(/\s+$/, "");
      // If the last thing is a section token like {uuid}, drop the entire
      // token instead of just the closing brace.
      if (s.endsWith("}")) {
        const openIdx = s.lastIndexOf("{");
        if (openIdx >= 0) return s.slice(0, openIdx).replace(/\s+$/, "");
      }
      return s.slice(0, -1);
    });

  const handleSubmit = async () => {
    setError(null);
    if (!title.trim()) {
      setError("Section title is required.");
      return;
    }
    if (type === "formula" && !formulaExpr.trim()) {
      setError("Formula expression is required.");
      return;
    }
    setSaving(true);
    try {
      await onSubmit({
        section_type: type,
        title: title.trim(),
        formula_expression: type === "formula" ? formulaExpr : null,
        show_percentage: showPct,
        percentage_of_section_id: showPct ? pctOfId : null,
      });
      onClose();
    } catch (e: any) {
      setError(e?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-lg w-full max-w-xl p-6 max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-800">
            {isEdit ? "Edit section" : "Add section"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Type<span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              {(["revenue", "expense", "formula"] as MisSectionType[]).map(
                (t) => (
                  <button
                    key={t}
                    type="button"
                    disabled={isEdit}
                    onClick={() => setType(t)}
                    className={`flex-1 border rounded px-3 py-2 text-xs text-left ${
                      type === t
                        ? "border-myPrimaryColor bg-myPrimaryColor bg-opacity-10 text-myPrimaryColor font-semibold"
                        : "border-gray-300 text-gray-600 hover:border-gray-400"
                    } ${isEdit ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    {SECTION_TYPE_LABEL[t]}
                  </button>
                ),
              )}
            </div>
            {isEdit && (
              <p className="mt-1 text-xs text-gray-400 italic">
                Section type cannot be changed after creation.
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              {SECTION_TYPE_HINT[type]}
            </p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Title<span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={
                type === "revenue"
                  ? "Revenue"
                  : type === "expense"
                    ? "Cost of Goods Sold"
                    : "CM1 (a - b)"
              }
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-myPrimaryColor"
            />
          </div>

          {type === "formula" && (
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Formula expression<span className="text-red-500">*</span>
              </label>
              <div className="bg-gray-50 border border-gray-300 rounded p-3 space-y-2">
                <div className="min-h-[36px] flex items-center gap-2 flex-wrap">
                  <span className="text-lg font-semibold text-gray-700">
                    {renderedFormula || (
                      <span className="text-gray-400 text-sm">
                        Pick sections + operators below to build the formula
                      </span>
                    )}
                  </span>
                </div>

                {earlierSections.length > 0 && (
                  <div>
                    <div className="text-[10px] text-gray-500 uppercase font-semibold mb-1">
                      Sections
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {earlierSections.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => insertToken(s.id)}
                          className="border border-gray-300 hover:border-myPrimaryColor hover:bg-myPrimaryColor hover:bg-opacity-10 rounded px-2 py-1 text-xs"
                          title={s.title}
                        >
                          <span className="font-mono font-bold">
                            {orderToLetter(s.display_order)}
                          </span>
                          <span className="text-gray-500 ml-1">
                            {s.title}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <div className="text-[10px] text-gray-500 uppercase font-semibold mb-1">
                    Operators
                  </div>
                  <div className="flex gap-1">
                    {OPERATORS.map((op) => (
                      <button
                        key={op}
                        type="button"
                        onClick={() => insertOperator(op)}
                        className="border border-gray-300 hover:border-myPrimaryColor rounded px-3 py-1 text-sm font-mono"
                      >
                        {op}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={backspace}
                      className="border border-gray-300 hover:border-red-400 rounded px-3 py-1 text-xs text-gray-600"
                    >
                      ⌫
                    </button>
                    <button
                      type="button"
                      onClick={clearFormula}
                      className="border border-gray-300 hover:border-red-400 rounded px-3 py-1 text-xs text-gray-600"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Formula can only reference sections above this one.
              </p>
            </div>
          )}

          <div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={showPct}
                onChange={(e) => setShowPct(e.target.checked)}
                className="rounded"
              />
              Show a percentage row (e.g. CM1%)
            </label>
            {showPct && (
              <div className="mt-2">
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Percent of
                </label>
                <select
                  value={pctOfId ?? ""}
                  onChange={(e) => setPctOfId(e.target.value || null)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-myPrimaryColor"
                >
                  <option value="">— pick a section —</option>
                  {earlierSections.map((s) => (
                    <option key={s.id} value={s.id}>
                      {orderToLetter(s.display_order)} · {s.title}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Percent row = (this section ÷ percent-of section) × 100
                </p>
              </div>
            )}
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 text-sm font-semibold text-white bg-myPrimaryColor hover:bg-mySecondaryColor rounded disabled:opacity-50"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Add section"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SectionModal;
